import { AssetSetsList } from '@/components/features/projectDashboard/AssetSetsList'
import type { AssetSetSummary } from '@/components/features/projectDashboard/AssetSetsList'
import { IntegrationsPanel } from '@/components/features/projectDashboard/IntegrationsPanel'
import { Outputs } from '@/components/features/projectDashboard/Outputs'
import type {
  OutputSet,
  OutputSourceCapture,
} from '@/components/features/projectDashboard/Outputs'
import { RecentActivity } from '@/components/features/projectDashboard/RecentActivity'
import { SpatialCoverage } from '@/components/features/projectDashboard/SpatialCoverage'
import type { DroneCaptureSet } from '@/components/features/projectDashboard/SpatialCoverage'
import { TaskPipeline } from '@/components/features/projectDashboard/TaskPipeline'
import { buildActivityItems } from '@/components/features/projectDashboard/buildActivityItems'
import { NewTaskButton } from '@/components/features/taskCreate/NewTaskButton'
import { useClient } from '@/context/ClientContext'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { useProjectSummary } from '@/hooks/useProjectSummary'
import { PlusIcon } from '@heroicons/react/24/outline'
import {
  createFileRoute,
  useLoaderData,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { Button } from '@droidmap/frontend-ui'
import { TaskStatus } from '@droidmap/project-service-contract'
import { AssetSetType, StorageSummary } from '@droidmap/shared-service-contract'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId/',
)({
  component: ProjectDashboardPage,
})

const PARENT_ROUTE_ID = '/_authenticated/orgs/$orgId_/projects/$projectId'

const ASSET_SETS_VISIBLE = 4

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

function ProjectDashboardPage() {
  const { orgId, projectId } = Route.useParams()
  const { project } = useLoaderData({ from: PARENT_ROUTE_ID })
  const { metadata } = Route.useRouteContext()
  const navigate = useNavigate()
  const { organisationClient, userClient } = useClient()

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const summary = useProjectSummary(projectId, metadata)
  // Recent page of sets (newest first) — enough for the top-N panels below.
  const assetSets = useMemo(
    () => summary.assetSets.data?.items ?? [],
    [summary.assetSets.data],
  )
  const tasks = useMemo(() => summary.tasks.data ?? [], [summary.tasks.data])
  // Only active tokens count as "configured". useProjectSummary fetches all
  // statuses (active/expired/revoked) so it can drive the per-status
  // breakdown elsewhere; using the raw .data length here would mark XYZ
  // Tile as configured even for projects that only have revoked or expired
  // tokens.
  const tokenCount = summary.counts.tokenActive

  const assetSetSummaries: AssetSetSummary[] = useMemo(
    () =>
      assetSets
        .map((set) => ({
          id: set.id,
          name: set.name,
          type: set.assetSetType,
          assetCount: set.assetCount ?? 0,
          bytes: totalStorageBytes(set.storageSummary),
          created: new Date(set.created),
        }))
        .sort((a, b) => b.created.getTime() - a.created.getTime()),
    [assetSets],
  )

  // Sourced from the dedicated drone query (not filtered out of the recent
  // page above), so an old drone capture is never missed.
  const droneSets: DroneCaptureSet[] = useMemo(
    () =>
      (summary.droneAssetSets.data ?? [])
        .map((s) => ({
          id: s.id,
          name: s.name,
          assetCount: s.assetCount ?? 0,
          created: new Date(s.created),
          // Persisted rollup (droidmap#2767/#2770), requested via
          // `includeSummaries`. Replaces the per-set asset walk the coverage
          // panel used to do, and is exact rather than a capped sample.
          bounds: s.summary?.boundingBox,
          capturedAtEarliest: s.summary?.capturedAtEarliest,
          capturedAtEarliestOffsetMinutes:
            s.summary?.capturedAtEarliestOffsetMinutes,
        }))
        .sort((a, b) => b.created.getTime() - a.created.getTime()),
    [summary.droneAssetSets.data],
  )

  // An asset set is "processing" iff it's the output of a task that is
  // still PENDING or RUNNING — drives the inline "processing…" badge in
  // the asset sets list.
  const processingSetIds = useMemo(() => {
    const ids = new Set<string>()
    for (const task of tasks) {
      if (
        task.status !== TaskStatus.PENDING &&
        task.status !== TaskStatus.RUNNING
      )
        continue
      for (const ref of task.outputAssetSetReferences ?? []) ids.add(ref.id)
    }
    return ids
  }, [tasks])

  const sortedTasks = useMemo(
    () =>
      [...tasks].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      ),
    [tasks],
  )

  // Captures highlighted across both panels. Empty array = nothing
  // currently highlighted. Either panel can drive this — clicking a
  // chip in the timeline pushes that day's member ids in; clicking a
  // polygon on the map pushes its id in. The initial "freshest capture"
  // default is seeded inside SpatialCoverage, which keys off capture
  // (flight) dates rather than the upload-date order available here.
  const [highlightedCaptureIds, setHighlightedCaptureIds] = useState<
    readonly string[]
  >([])

  // Output-set id → originating capture, derived from task lineage
  // (`task.inputAssetSetReferences` of type DroneImages →
  // `task.outputAssetSetReferences`). Lets the Recent Assets panel
  // surface the capture date on each card; capture dates and
  // output-set creation dates are usually months apart, so showing
  // both keeps the link between timeline and cards visible.
  const captureBySetId = useMemo(() => {
    const captureLookup = new Map<string, OutputSourceCapture>(
      droneSets.map((s) => [s.id, { id: s.id, name: s.name, date: s.created }]),
    )
    const out = new Map<string, OutputSourceCapture>()
    for (const task of tasks) {
      const droneInput = task.inputAssetSetReferences?.find(
        (r) => r.assetSetType === AssetSetType.DroneImages,
      )
      if (!droneInput) continue
      const capture = captureLookup.get(droneInput.id)
      if (!capture) continue
      for (const ref of task.outputAssetSetReferences ?? []) {
        // First-write-wins: a single output set is usually produced by
        // exactly one task, but if multiple tasks reference the same
        // output we keep the earliest-encountered capture rather than
        // overwriting back and forth.
        if (!out.has(ref.id)) out.set(ref.id, capture)
      }
    }
    return out
  }, [tasks, droneSets])

  // All asset sets sorted by created desc. The panel filters internally
  // by content kind (Ortho / DSM / DTM / 3D / Drone) using each set's
  // declared `containedAssetTypes`.
  const outputSets: OutputSet[] = useMemo(
    () =>
      assetSets
        .map((s) => ({
          id: s.id,
          name: s.name,
          created: new Date(s.created),
          type: s.assetSetType,
          containedAssetTypes: s.containedAssetTypes ?? [],
        }))
        .sort((a, b) => b.created.getTime() - a.created.getTime()),
    [assetSets],
  )

  const activity = useMemo(
    () => buildActivityItems({ tasks, assetSets, project, userMap }),
    [tasks, assetSets, project, userMap],
  )

  const handleCreateAssetSet = () => {
    void navigate({
      to: '/orgs/$orgId/projects/$projectId/asset-sets/create',
      params: { orgId, projectId },
    })
  }

  // Empty-state CTA inside TaskPipeline — lands on the type-selection cards.
  const handleCreateTask = () => {
    void navigate({
      to: '/orgs/$orgId/projects/$projectId/tasks/create',
      params: { orgId, projectId },
    })
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={handleCreateAssetSet}>
          <PlusIcon className="h-4 w-4" />
          Asset Set
        </Button>
        <NewTaskButton orgId={orgId} projectId={projectId} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <SpatialCoverage
            sets={droneSets}
            orgId={orgId}
            projectId={projectId}
            highlightedIds={highlightedCaptureIds}
            onHighlightChange={setHighlightedCaptureIds}
            isLoading={summary.droneAssetSets.isLoading}
          />
          <Outputs
            sets={outputSets}
            captureBySetId={captureBySetId}
            orgId={orgId}
            projectId={projectId}
            metadata={metadata}
            isLoading={summary.assetSets.isLoading}
          />
          <AssetSetsList
            sets={assetSetSummaries.slice(0, ASSET_SETS_VISIBLE)}
            processingSetIds={processingSetIds}
            orgId={orgId}
            projectId={projectId}
            isLoading={summary.assetSets.isLoading}
          />
        </div>
        <div className="space-y-6 lg:col-span-2">
          <TaskPipeline
            tasks={sortedTasks}
            userMap={userMap}
            orgId={orgId}
            projectId={projectId}
            onCreateTask={handleCreateTask}
            isLoading={summary.tasks.isLoading}
          />
          <IntegrationsPanel
            tokenCount={tokenCount}
            orgId={orgId}
            projectId={projectId}
            isLoading={summary.tokens.isLoading}
          />
          <RecentActivity
            items={activity}
            // Activity is built from tasks + assetSets + project; show
            // "Loading…" while either of the underlying queries is fetching.
            isLoading={summary.tasks.isLoading || summary.assetSets.isLoading}
          />
        </div>
      </div>
    </div>
  )
}
